/**
 * Transforms bare citation tokens the model emits into real markdown links
 * so BlockSuite's markdown adapter renders them as clickable anchors.
 *
 *   [PMID:12345]        → [PMID:12345](https://pubmed.ncbi.nlm.nih.gov/12345/)
 *   [PMID: 12345]       → same (tolerates whitespace after the colon)
 *   [DOI:10.1234/abc]   → [DOI:10.1234/abc](https://doi.org/10.1234/abc)
 *   [arXiv:1706.03762]  → [arXiv:1706.03762](https://arxiv.org/abs/1706.03762)
 *
 * Existing markdown links `[text](url)` are left untouched via the negative
 * lookahead — the regex only fires when the `]` is NOT followed by `(`.
 * Other tokens — `[NEEDS-CITATION:…]`, `[doc:p.N]`, `[N]` — are intentionally
 * ignored; they have no authoritative URL target.
 *
 * Safe for streaming: runs in one pass on the full accumulated text.
 * Partial tokens (`[PMID:12` without closing bracket) are skipped; a later
 * chunk completes and linkifies them naturally.
 */

import type { Citation } from '../types';

export interface LinkifyOptions {
  /**
   * Optional citations map. When provided together with `useJournalLabel`,
   * verified citations render the link text as the journal name instead of
   * `PMID:xxxxx` — matching Claude's inline "[Nature]" chip style.
   */
  citations?: Citation[];
  /**
   * When true and a verified citation with a `journal` is available, the
   * rendered markdown link label becomes the journal name. Falls back to
   * `PMID:xxxxx` if no journal is known.
   */
  useJournalLabel?: boolean;
  /**
   * Maps a citation's canonical key (see `citationDisplayKey`) to its
   * 1-based number in the Sources list. When supplied, inline tokens
   * render as a linked `[n]` index matching the corresponding Sources
   * entry — takes precedence over `useJournalLabel`.
   */
  numberByKey?: ReadonlyMap<string, number>;
}

const TOKEN_RE = /\[(PMID|DOI|arXiv)\s*:\s*([^\]]+?)\](?!\()/gi;

export function linkifyCitations(
  markdown: string,
  options: LinkifyOptions = {}
): string {
  if (!markdown) return markdown;
  const byKey = buildCitationMap(options.citations);
  const numberByKey = options.numberByKey;
  return markdown.replace(TOKEN_RE, (match, rawKind: string, rawId: string) => {
    const kind = rawKind.toLowerCase();
    const id = rawId.trim();
    if (!id) return match;
    let url: string;
    switch (kind) {
      case 'pmid':
        if (!/^\d+$/.test(id)) return match;
        url = `https://pubmed.ncbi.nlm.nih.gov/${id}/`;
        break;
      case 'doi':
        if (!/^10\.\d{4,}\//.test(id)) return match;
        url = `https://doi.org/${id}`;
        break;
      case 'arxiv':
        if (!/^[\d.]+(v\d+)?$/.test(id)) return match;
        url = `https://arxiv.org/abs/${id}`;
        break;
      default:
        return match;
    }
    // Prefer a compact numbered `[n]` index that matches the Sources
    // list below; the same source reused gets the same number.
    const n = numberByKey?.get(citationDisplayKey(kind, id));
    if (n !== undefined) {
      return `[[${n}]](${url})`;
    }
    let label = match.slice(1, -1); // default preserves "PMID: 12345"
    if (options.useJournalLabel && byKey) {
      const found = byKey.get(citationDisplayKey(kind, id));
      if (found?.verified && found.journal) label = found.journal;
    }
    return `[${label}](${url})`;
  });
}

function buildCitationMap(
  citations?: Citation[]
): Map<string, Citation> | null {
  if (!citations?.length) return null;
  const map = new Map<string, Citation>();
  for (const c of citations) {
    if (!c.identifier || !c.type) continue;
    map.set(citationDisplayKey(c.type, c.identifier), c);
  }
  return map;
}

/**
 * Canonical key for matching inline tokens to citations and Sources
 * entries. Normalises `pubmed`→`pmid` and collapses arXiv version
 * suffixes so `[arXiv:x]` and `[arXiv:xv2]` share one number.
 */
export function citationDisplayKey(
  type: string | undefined,
  identifier: string
): string {
  const t = (type ?? '').toLowerCase();
  const kind = t === 'pubmed' ? 'pmid' : t;
  const id =
    kind === 'arxiv'
      ? identifier.trim().toLowerCase().replace(/v\d+$/i, '')
      : identifier.trim().toLowerCase();
  return `${kind}:${id}`;
}

/**
 * Deduplicate citations by canonical key, preserving first-seen order.
 * The single source of ordering for both the numbered inline markers and
 * the Sources list, so their numbers always agree.
 */
export function dedupeCitationsForDisplay(citations: Citation[]): Citation[] {
  const seen = new Set<string>();
  const out: Citation[] = [];
  for (const c of citations) {
    if (!c.identifier) continue;
    const key = citationDisplayKey(c.type, c.identifier);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(c);
  }
  return out;
}

/** Map each unique citation's canonical key to its 1-based Sources number. */
export function buildCitationNumberMap(
  citations: Citation[]
): Map<string, number> {
  const map = new Map<string, number>();
  dedupeCitationsForDisplay(citations).forEach((c, i) => {
    if (c.identifier) {
      map.set(citationDisplayKey(c.type, c.identifier), i + 1);
    }
  });
  return map;
}
